import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchWithRetry, MAX_RETRY_ATTEMPTS, RETRY_DELAYS } from '../../util/sliceUtils';
import config from '../../config';

const backendUrl = config.backendUrl;

const INTERSECTIONS_URL = backendUrl + '/intersections';

export const trafficInfraSlice = createSlice({
    name: 'trafficInfra',
    initialState: {
        intersections: {},
        topologies: {}, // Cache for topology data
        topologyRetries: {}, // Retry counts per intersection ID
        intersectionsLoading: false,
        retryCount: 0,
    },
    reducers: {
        setIntersections: (state, action) => {
            state.intersections = action.payload;
            state.retryCount = 0;
        },
        setTopology: (state, action) => {
            const { id, data } = action.payload;
            state.topologies[id] = data;
        },
        setIntersectionsLoading: (state, action) => {
            state.intersectionsLoading = action.payload;
        },
        incrementRetryCount: (state) => {
            state.retryCount += 1;
        },
        resetRetryCount: (state) => {
            state.retryCount = 0;
        },
        incrementTopologyRetry: (state, action) => {
            const id = action.payload;
            state.topologyRetries[id] = (state.topologyRetries[id] || 0) + 1;
        },
        resetTopologyRetry: (state, action) => {
            const id = action.payload;
            state.topologyRetries[id] = 0;
        },
    },
});

export const {
    setIntersections,
    setTopology,
    setIntersectionsLoading,
    incrementRetryCount,
    resetRetryCount,
    incrementTopologyRetry,
    resetTopologyRetry,
} = trafficInfraSlice.actions;

// Retry thunk for manual retry from toast - this is what gets dispatched when clicking retry
export const retryIntersections = () => (dispatch) => {
    console.log('Manually retrying intersections...');
    dispatch(resetRetryCount());
    dispatch(getIntersections(true));
};

export const getIntersections = (forceRetry = false) => {
    return fetchWithRetry({
        fetchFn: () => axios.get(`${INTERSECTIONS_URL}`, { headers: {} }),
        actions: {
            setData: setIntersections,
            setLoading: setIntersectionsLoading,
            incrementRetry: incrementRetryCount,
        },
        selectors: {
            selectRetryCount: selectRetryCount,
            selectLoading: selectIntersectionsLoading,
        },
        retryActionName: 'RETRY_INTERSECTIONS',
        errorMessagePrefix: 'Failed to load intersections',
        retryThunk: getIntersections,
    }, forceRetry);
};

export const fetchTopology = (intersectionId, fallbackId = null, forceRetry = false) => {
    return fetchWithRetry({
        fetchFn: async () => {
            try {
                return await axios.get(`${backendUrl}/intersections/${intersectionId}/topology`);
            } catch (error) {
                // If 404 and we have a fallback ID (e.g. shared device ID like 536 for 536b)
                if (fallbackId && (error.response?.status === 404 || error.code === 'ERR_BAD_REQUEST')) {
                    console.warn(`Topology for ${intersectionId} not found, trying fallback ID: ${fallbackId}`);
                    return await axios.get(`${backendUrl}/intersections/${fallbackId}/topology`);
                }
                throw error;
            }
        },
        actions: {
            setData: (data) => setTopology({ id: intersectionId, data }),
            setLoading: (isLoading) => {
                if (isLoading) {
                    return setTopology({ id: intersectionId, data: { status: 'LOADING' } });
                }
                // Return a no-op action when loading is false, as we don't want to clear the data/status yet
                return { type: 'trafficInfra/setLoading_NoOp' };
            },
            incrementRetry: () => incrementTopologyRetry(intersectionId),
            setError: (msg) => setTopology({ id: intersectionId, data: { status: 'ERROR', error: msg } }),
        },
        selectors: {
            selectRetryCount: (state) => state.trafficInfra.topologyRetries[intersectionId] || 0,
            selectLoading: (state) => {
                const topology = state.trafficInfra.topologies[intersectionId];
                return topology && topology.status === 'LOADING';
            },
        },
        retryActionName: `RETRY_TOPOLOGY_${intersectionId}`,
        errorMessagePrefix: `Failed to load topology for ${intersectionId}`,
        retryThunk: (force) => fetchTopology(intersectionId, fallbackId, force),
    }, forceRetry);
};

// Selector to check if a specific topology is loading
export const selectIsTopologyLoading = (state, id) => {
    const topology = state.trafficInfra.topologies[id];
    return topology && topology.status === 'LOADING';
};

export const selectIntersections = state => state.trafficInfra.intersections;
export const selectTopologies = state => state.trafficInfra.topologies;
export const selectIntersectionsLoading = state => state.trafficInfra.intersectionsLoading;
export const selectRetryCount = state => state.trafficInfra.retryCount;

export default trafficInfraSlice.reducer;
